'use client';

import React, { useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ChartSkeleton } from './ChartSkeleton';
import { CustomLegend } from './combined/CustomLegend';
import { theme } from '../../config/theme';
import type { ChartData, ChartLayout, ChartConfig } from '../../types/chart';
import { Breadcrumbs } from '../ui-components';

const BarChartContainer = styled.div<{ $isFullscreen: boolean }>`
  overflow: visible;
  transition: all 0.3s ease;
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  
  ${({ $isFullscreen }) =>
    $isFullscreen &&
    `
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;
    padding: 40px;
    background: ${theme.colors.white};
  `}
`;

const ChartHeader = styled.div`
  padding: 16px 20px 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ChartDescription = styled.p`
  display: block;
  margin: 4px 0 0 0;
  font-size: 0.875rem;
  color: ${theme.colors.gray[500]};
`;

const ChartContent = styled.div<{ $minHeight?: number }>`
  padding: 8px 16px 0px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: ${({ $minHeight }) => $minHeight ? `${$minHeight}px` : '250px'};
  max-height: 100%; /* Prevent flex item from growing beyond container */
  overflow: hidden; /* Prevent content from overflowing */
  
  /* Chart should take all available space */
  & > div {
    flex: 1;
    height: 100%;
    min-height: 0; /* Ensure chart respects container constraints */
  }
  
  /* Remove hover border */
  &:hover {
    border: none !important;
    outline: none !important;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.gray[200]};
  border-top-color: ${theme.colors.primary.accent};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ChartContentWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Prevent flex item from growing beyond container */
  overflow: hidden; /* Prevent content from overflowing */
`;

export interface BarChartData {
  categories: string[];
  series: {
    name: string;
    data: number[];
    color?: string;
  }[];
}

export interface BarChartProps {
  title: string;
  description?: string;
  data: BarChartData;
  orientation?: 'horizontal' | 'vertical';
  stacked?: boolean;
  showBorder?: boolean;
  height?: number;
  minHeight?: number; // Minimum height for chart content (default: 250px)
  size?: 'small' | 'medium' | 'large'; // Chart size (default: 'medium')
  loading?: boolean;
  className?: string;
  yAxisTickFormatter?: (value: number) => string;
  barSize?: number;
  barWidth?: number; // Bar width relative to category spacing (default: 0.3)
  tooltipLabel?: string; // Custom label for tooltip (e.g., 'Error count', 'Response time')
  yAxisLabel?: string; // Custom label for Y-axis (e.g., 'Error count')
  xAxisLabel?: string; // Custom label for X-axis (e.g., 'Day Quarter')
  legendTooltipLabel?: string; // Custom label for legend tooltip (default: 'count')
  yAxisLabelStandoff?: number; // Spacing between y-axis label and axis (default: 0)
  tooltipFormatter?: (value: number) => string; // Custom function to format tooltip values
  headerControls?: React.ReactNode; // Optional header controls (e.g., time window buttons)
  style?: React.CSSProperties;
  customTooltip?: React.ComponentType<any>; // Custom tooltip component
  tooltipData?: any; // Additional data to pass to custom tooltip
  onBarClick?: (event: { activeLabel?: string | number; activePayload?: Array<{ name: string; value: number | string }> }) => void; // Handler for bar click events
  legendFontSize?: string; // Custom font size for legend labels (default: '10px')
  legendFontWeight?: number; // Custom font weight for legend labels (default: 500)
  xAxisLabelFontSize?: number; // Custom font size for x-axis label (default: 12)
  yAxisLabelFontSize?: number; // Custom font size for y-axis label (default: 12)
  totalChecks?: number; // Total checks count for table view title
  period?: string; // Time period for table view title
  categoryTimeRanges?: Record<string, string>; // Map of category to time range for table view
  xAxisTickFormat?: (value: string | number) => string; // Custom function to format x-axis tick labels
  /** Called when fullscreen state changes (so parent can avoid unmounting when loading in fullscreen) */
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

export const BarChart: React.FC<BarChartProps> = ({
  title,
  description,
  data,
  orientation = 'vertical',
  stacked = false,
  customTooltip,
  tooltipData,
  showBorder = true,
  height = 400,
  minHeight = 250,
  size = 'medium',
  loading = false,
  className,
  yAxisTickFormatter,
  barSize,
  barWidth = 0.3,
  tooltipLabel,
  yAxisLabel,
  xAxisLabel,
  legendTooltipLabel = 'count',
  yAxisLabelStandoff = 0,
  tooltipFormatter,
  headerControls,
  style,
  onBarClick,
  legendFontSize = '10px',
  legendFontWeight = 500,
  xAxisLabelFontSize = 12,
  yAxisLabelFontSize = 12,
  totalChecks,
  period,
  categoryTimeRanges,
  xAxisTickFormat,
  onFullscreenChange,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartKey, setChartKey] = useState(0);
  const isFullscreenRef = useRef(false);
  const onFullscreenChangeRef = useRef(onFullscreenChange);
  onFullscreenChangeRef.current = onFullscreenChange;

  // Monitor fullscreen changes for THIS specific chart only
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!chartRef.current) return;
      
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        mozFullScreenElement?: Element;
        msFullscreenElement?: Element;
      };
      
      const fullscreenElement = 
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;
      
      // Only update if THIS chart is the one in fullscreen (or exiting fullscreen)
      const isThisChartFullscreen = fullscreenElement === chartRef.current;
      const wasThisChartFullscreen = isFullscreen;
      
      if (isThisChartFullscreen !== wasThisChartFullscreen) {
        setIsFullscreen(isThisChartFullscreen);
        isFullscreenRef.current = isThisChartFullscreen;
        onFullscreenChangeRef.current?.(isThisChartFullscreen);
        // Force re-render by changing key
        setChartKey(prev => prev + 1);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Check if we're currently in fullscreen (to preserve fullscreen during loading)
  // Use useMemo to avoid calling this during render and prevent setState-in-render errors
  const isCurrentlyFullscreen = useMemo(() => {
    // First check the ref (most up-to-date)
    if (isFullscreenRef.current) return true;
    
    // Then check state
    if (isFullscreen) return true;
    
    // Finally check DOM directly as fallback (only if we're on client side)
    if (typeof window === 'undefined' || !chartRef.current) return false;
    
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      mozFullScreenElement?: Element;
      msFullscreenElement?: Element;
    };
    
    const fullscreenElement = 
      document.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement;
    
    return fullscreenElement === chartRef.current;
  }, [isFullscreen]);

  // Adjust height based on fullscreen state
  // Use useMemo to stabilize height calculation and prevent unexpected changes during loading
  // Check both state and ref to ensure we have the most up-to-date fullscreen state
  // MUST be before early return to ensure hooks are called in consistent order
  const chartHeight = useMemo(() => {
    const currentlyFullscreen = isFullscreenRef.current || isFullscreen;
    if (currentlyFullscreen && typeof window !== 'undefined') {
      // Calculate height accounting for header, padding, and legend
      // Header: ~60px, Padding: 40px top + 40px bottom = 80px, Legend: ~50px
      // Total: ~190px, use 200px for safety margin
      return window.innerHeight - 200;
    }
    return height;
  }, [isFullscreen, height]);

  // Show skeleton if loading, but only if NOT in fullscreen (to preserve fullscreen state)
  // This early return MUST come after all hooks to avoid "Rendered fewer hooks than expected" error
  if (loading && !isCurrentlyFullscreen) {
    return <ChartSkeleton type="bar" height={`${height}px`} items={data.categories?.length||4} />;
  }

  const defaultColors = [
    theme.colors.primary[500],
    theme.colors.success,
    theme.colors.warning,
    theme.colors.error,
    theme.colors.primary[300],
    '#8b5cf6',
  ];

  // Calculate maximum value across all bars for transparent overlay
  const maxValue = Math.max(...data.series.flatMap(s => s.data), 1);

  // Create actual bar data
  const chartData: ChartData[] = data.series.map((series, index) => {
    // Create hovertemplate with formatted values
    const createHoverTemplate = (value: number) => {
      const formattedValue = tooltipFormatter 
        ? tooltipFormatter(value)
        : (value >= 1000 
            ? `${(value / 1000).toFixed(1)}k` 
            : value.toLocaleString());
      
      if (tooltipLabel) {
        return orientation === 'horizontal'
          ? `<b>%{fullData.name}</b><br>${tooltipLabel}: ${formattedValue}<extra></extra>`
          : `<b>%{fullData.name}</b><br>${tooltipLabel}: ${formattedValue}<extra></extra>`;
      }
      return orientation === 'horizontal'
        ? `<b>%{fullData.name}</b><br>%{y}: ${formattedValue}<extra></extra>`
        : `<b>%{fullData.name}</b><br>%{x}: ${formattedValue}<extra></extra>`;
    };

    return {
      x: orientation === 'vertical' ? data.categories : series.data,
      y: orientation === 'vertical' ? series.data : data.categories,
      type: 'bar',
      orientation: orientation === 'horizontal' ? 'h' : 'v',
      name: series.name,
      // Note: width property doesn't work in Plotly - bar width is controlled by barCategoryGap in layout
      showlegend: false, // Hide bar legend (we'll use scatter traces for circles)
      marker: {
        color: series.color || defaultColors[index],
        line: {
          color: 'transparent', // Remove border on hover
          width: 0, // No border
        },
      },
      hoverinfo: 'x+y+name',
      // Use customtext to pass formatted values, then reference in hovertemplate
      customdata: orientation === 'vertical' 
        ? series.data.map(val => createHoverTemplate(val))
        : data.categories.map(() => createHoverTemplate(series.data[0] || 0)),
      hovertemplate: '%{customdata}',
    };
  });

  // Create transparent overlay bars to make all bars reach 100% (maxValue)
  // Only create overlays when NOT in stacked mode (stacked bars don't need overlays)
  const transparentOverlayData: ChartData[] = stacked ? [] : data.series.map((series, seriesIndex) => {
    // Create overlay values: difference between maxValue and actual value
    const overlayValues = series.data.map(value => value > 0 ? maxValue - value : 0);
    
    // For overlay mode, we need to set base to the actual bar values so overlays stack on top
    const baseValues = orientation === 'vertical' ? series.data : undefined;
    
    return {
      x: orientation === 'vertical' ? data.categories : overlayValues,
      y: orientation === 'vertical' ? overlayValues : data.categories,
      type: 'bar',
      orientation: orientation === 'horizontal' ? 'h' : 'v',
      name: `${series.name}_overlay`,
      // Note: width property doesn't work in Plotly - bar width is controlled by barCategoryGap in layout
      base: baseValues, // Start overlay from the top of the actual bar
      showlegend: false,
      marker: {
        color: 'rgba(0, 0, 0, 0)', // Fully transparent
        opacity: 0,
        line: {
          color: 'transparent',
          width: 0,
        },
      },
      hoverinfo: 'skip', // Don't show hover for transparent overlays
    };
  });

  // Detect if each series has only one non-zero value at different positions
  // This pattern suggests we should use 'overlay' mode for better alignment
  // But if stacked is true, we always use 'stack' mode
  const hasSingleValuePerSeries = !stacked && data.series?.length > 0 && data.series.every(series => {
    const nonZeroIndices = series.data.map((val, idx) => val > 0 ? idx : -1).filter(idx => idx >= 0);
    return nonZeroIndices?.length === 1;
  });
  
  // Determine barmode: if stacked is true, always use 'stack' mode
  // Otherwise, use overlay for single value per series, or group for multiple series
  const effectiveBarmode = stacked ? 'stack' : (hasSingleValuePerSeries ? 'overlay' : 'group');

  // Create annotations to display values below each bar (for vertical orientation)
  // For grouped bars, show each bar's individual value
  const annotations = orientation === 'vertical' 
    ? (() => {
        const annotationList: any[] = [];
        
        // If bars are grouped (multiple series with values at same categories), show each bar's value
        // Stacked bars show total value, not individual values
        if (!stacked && !hasSingleValuePerSeries && data.series?.length > 1) {
          // Grouped bars: create annotation for each series at each category
          data.categories.forEach((category, categoryIndex) => {
            data.series.forEach((series, seriesIndex) => {
              const value = series.data[categoryIndex] || 0;
              if (value > 0) {
                // Calculate x position offset for grouped bars
                // Bars are centered, so we need to offset based on series index
                const numSeries = data.series.length;
                const gap = 0.1; // Gap between bars
                const totalBarWidth = (barWidth * numSeries) + (gap * (numSeries - 1));
                const offset = (seriesIndex - (numSeries - 1) / 2) * (barWidth + gap);
                
                annotationList.push({
                  x: category,
                  xshift: offset * 100, // Convert to pixels (approximate)
                  y: -maxValue * 0.04,
                  text: value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toLocaleString(),
                  showarrow: false,
                  xref: 'x' as const,
                  yref: 'y' as const,
                  xanchor: 'center' as const,
                  yanchor: 'middle' as const,
                  font: {
                    size: 11,
                    color: theme.colors.gray[700],
                  },
                });
              }
            });
          });
        } else if (hasSingleValuePerSeries) {
          // Single value per series at different positions: show each bar's value
          data.series.forEach((series, seriesIndex) => {
            const valueIndex = series.data.findIndex(val => val > 0);
            if (valueIndex >= 0 && valueIndex < data.categories?.length) {
              const value = series.data[valueIndex];
              annotationList.push({
                x: data.categories[valueIndex],
                y: -maxValue * 0.04,
                text: value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toLocaleString(),
                showarrow: false,
                xref: 'x' as const,
                yref: 'y' as const,
                xanchor: 'center' as const,
                yanchor: 'middle' as const,
                font: {
                  size: 11,
                  color: theme.colors.gray[700],
                },
              });
            }
          });
        } else {
          // Single series or stacked: show total value per category
          data.categories.forEach((category, categoryIndex) => {
            const totalValue = data.series.reduce((sum, series) => sum + (series.data[categoryIndex] || 0), 0);
            if (totalValue > 0) {
              annotationList.push({
                x: category,
                y: -maxValue * 0.04,
                text: totalValue >= 1000 ? `${(totalValue / 1000).toFixed(1)}k` : totalValue.toLocaleString(),
                showarrow: false,
                xref: 'x' as const,
                yref: 'y' as const,
                xanchor: 'center' as const,
                yanchor: 'middle' as const,
                font: {
                  size: 11,
                  color: theme.colors.gray[700],
                },
              });
            }
          });
        }
        
        return annotationList;
      })()
    : [];

  // Calculate maxBarSize
  // If hasSingleValuePerSeries is true, each series contributes one bar at a different category
  // So we don't need to multiply - just use the requested barSize
  // If bars are grouped (multiple bars per category), we need to account for that
  // Stacked bars are not grouped - they're stacked on top of each other
  const numSeries = data.series?.length;
  const isGroupedBars = !stacked && !hasSingleValuePerSeries && numSeries > 1;
  const calculatedMaxBarSize = barSize !== undefined && orientation === 'vertical'
    ? (isGroupedBars ? barSize * numSeries : barSize) // Only multiply if bars are actually grouped (not stacked)
    : barSize;

  // Convert barWidth to barCategoryGap and barGap
  // barWidth is a ratio (0-1) representing the proportion of category space bars should occupy
  // Smaller barWidth = narrower bars = larger gap
  // Larger barWidth = wider bars = smaller gap
  // barCategoryGap is the gap between categories (as percentage or number)
  // For vertical bars: if barWidth is provided, use it to calculate gap
  const calculateBarGaps = () => {
    if (orientation !== 'vertical') return {};
    
    // If barWidth is provided, use it to control bar width via gaps
    // barWidth of 0.1 means bars take 10% of category space, so gap is 90%
    // barWidth of 0.9 means bars take 90% of category space, so gap is 10%
    if (barWidth !== undefined) {
      // Convert barWidth (0-1) to gap percentage
      // Formula: gapPercentage = (1 - barWidth) * 100
      // Clamp barWidth to valid range (0.05 to 0.95) to avoid extreme values
      const clampedBarWidth = Math.max(0.05, Math.min(0.95, barWidth));
      const gapPercentage = (1 - clampedBarWidth) * 100;
      return {
        barCategoryGap: `${Math.max(0.1, Math.min(95, gapPercentage))}%`, // Clamp gap between 0.1% and 95%
        barGap: stacked ? 0 : (isGroupedBars ? 5 : 10), // No gap for stacked bars, small gap for grouped
      };
    }
    
    // Default behavior: use barSize-based gaps if barSize is set, otherwise use defaults
    if (barSize !== undefined) {
      return {
        barCategoryGap: isGroupedBars ? '2%' : '5%',
        barGap: isGroupedBars ? 10 : 30,
      };
    }
    
    return {};
  };

  // Check if x-axis has a label (for vertical orientation, use xAxisLabel if provided, otherwise empty)
  const xAxisTitleText = orientation === 'vertical' 
    ? (xAxisLabel || '') 
    : (xAxisLabel || 'Values');
  const xAxisHasLabel = xAxisTitleText && xAxisTitleText?.trim() !== '';

  const layout: ChartLayout = {
    height: chartHeight,
    barmode: effectiveBarmode,
    hovermode: 'closest',
    ...(calculatedMaxBarSize !== undefined && { barSize: calculatedMaxBarSize, maxBarSize: calculatedMaxBarSize }),
    // Apply bar gaps based on barWidth or barSize
    ...calculateBarGaps(),
    margin: {
      l: orientation === 'horizontal' ? 120 : (yAxisLabel ? 20 : 40), // Minimal left margin when y-axis label is present
      r: 20,
      t: 10,
      b: orientation === 'horizontal' ? 60 : (xAxisHasLabel ? 50 : 40), // Reduced bottom margin when no x-axis label
    },
    annotations: annotations,
    xaxis: {
      showgrid: true,
      gridcolor: theme.colors.gray[200],
      title: {
        text: xAxisTitleText,
        font: { size: xAxisLabelFontSize, color: theme.colors.gray[700] },
      },
      type: orientation === 'vertical' ? 'category' : 'linear', // Use category type for proper alignment
      // Ensure proper spacing and alignment for category axis
      ...(orientation === 'vertical' && {
        categoryorder: 'array',
        categoryarray: data.categories,
      }),
      ...(xAxisTickFormat ? {
        tickFormatter: xAxisTickFormat,
      } : {}),
    },
    yaxis: {
      showgrid: true,
      gridcolor: theme.colors.gray[200],
      title: {
        text: yAxisLabel || (orientation === 'vertical' ? '' : 'Categories'),
        font: { size: yAxisLabelFontSize, color: theme.colors.gray[700] },
        standoff: yAxisLabelStandoff, // Configurable spacing between y-axis label and axis (Plotly)
        offset: yAxisLabelStandoff, // Configurable offset for Recharts YAxis label
      },
      rangemode: 'tozero', // Start Y-axis from 0
      automargin: false, // Disable auto margin to use our custom margin (only for vertical orientation)
      // For horizontal bars, ensure category labels are visible
      ...(orientation === 'horizontal' && {
        automargin: true,
      }),
      // Add tick formatter if provided
      ...(yAxisTickFormatter && {
        tickFormatter: yAxisTickFormatter,
      }),
    } as any,
    showlegend: false, // Hide legend, use custom legend instead
  };

  const config: ChartConfig = {
    displayModeBar: false,
    displaylogo: false,
    staticPlot: !zoomEnabled,
    responsive: true,
    scrollZoom: zoomEnabled,
    doubleClick: zoomEnabled ? 'reset' : false,
    modeBarButtonsToRemove: zoomEnabled 
      ? ['lasso2d', 'select2d', 'autoScale2d', 'resetScale2d']
      : ['lasso2d', 'select2d', 'pan2d', 'zoom2d', 'autoScale2d', 'resetScale2d'],
  };

  // Create table data for hamburger menu
  const tableData: Array<Record<string, string | number>> = [];
  data.categories.forEach((category, index) => {
    data.series.forEach(series => {
      // Include time range in brackets if available
      const timeRange = categoryTimeRanges?.[category];
      const categoryDisplay = timeRange ? `${category} (${timeRange})` : category;
      tableData.push({
        category: categoryDisplay,
        series: series.name,
        value: series.data[index]
      });
    });
  });

  // No data when no series, or when every trace has x and y completely null/empty
  const isAxisEmpty = (axis: ChartData['x'] | ChartData['y']): boolean =>
    axis == null ||
    !Array.isArray(axis) ||
    axis.length === 0 ||
    axis.every((v) => v == null || (typeof v === 'number' && Number.isNaN(v)));

  const noData =
    chartData?.length === 0 ||
    chartData.every(
      (trace) => isAxisEmpty(trace.x) && isAxisEmpty(trace.y)
    );

  return (
        <BarChartContainer ref={chartRef} $isFullscreen={isFullscreen} className={className} style={style}>
          <ChartHeader>
        <HeaderContent>
        <Breadcrumbs
                  items={[{ label: title }]} 
                  style={{padding: '0px'}}
                  />
              {description && <ChartDescription>{description}</ChartDescription>}
        </HeaderContent>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {headerControls}
          <ChartHamburgerMenu 
            chartTitle={title}
            tableData={tableData}
            chartContainerRef={chartRef}
            onZoomToggle={setZoomEnabled}
            initialZoomEnabled={zoomEnabled}
            totalChecks={totalChecks}
            period={period}
            showTableData={false}
          />
        </div>
      </ChartHeader>
      
      <ChartContentWrapper>
        <ChartContent $minHeight={minHeight}>
              <Chart
                key={chartKey}
                data={[...chartData, ...transparentOverlayData]}
                layout={layout}
                config={config}
                revision={chartKey}
                tooltipLabel={tooltipLabel}
                tooltipFormatter={tooltipFormatter}
                size={size}
                customTooltip={customTooltip}
                tooltipData={tooltipData}
                onClick={onBarClick}
              />
        </ChartContent>
        {loading && isCurrentlyFullscreen && (
          <LoadingOverlay>
            <LoadingSpinner />
          </LoadingOverlay>
        )}
      </ChartContentWrapper>

      <CustomLegend
        series={data.series.map((series, index) => ({
          name: series.name,
          color: series.color || defaultColors[index],
          data: series.data,
        }))}
        size={size}
        legendTooltipLabel={legendTooltipLabel}
        legendFontSize={legendFontSize}
        legendFontWeight={legendFontWeight}
        defaultColors={defaultColors}
        style={{padding: '0px 12px 24px 12px', marginTop: '-2px', paddingTop:noData?'24px':'0' }}
      />

    </BarChartContainer>
  );
};